import { Button } from '@/components/ui/button';
import { PlusIcon } from '@radix-ui/react-icons';
import dayjs from 'dayjs';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { fDate } from '@/utils/formatTime';
import CopyIconButton from '@/components/ui/copy-to-clipboard/copy-icon-button';
import { MdDeleteOutline } from 'react-icons/md';
import ConfirmationDialog from '@/components/ui/dialog-wrapper/ConfirmationDialog';
import CreateAPIKey from './CreateAPIKey';
import {
  useDeleteLLMApiKey,
  useGetLLMApiKeys,
} from '@/service/llm/api-key/hook';

import { Skeleton } from '@/components/ui/skeleton';
import { formatAPIKey, isNotEmpty } from '@/utils';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import { toast } from 'sonner';
import { toastSomethingError } from '@/components/ui/toast-content';

const LLMCredentials = () => {
  const { workspaceId } = useParamWorkspaceId();
  const { data, isLoading } = useGetLLMApiKeys({
    workspace_id: workspaceId,
  });

  const { mutate } = useDeleteLLMApiKey({
    onSuccess: async () => {
      toast.message('API Key Revoke');
    },
    onError: () => {
      toastSomethingError();
    }
  });

  const handledDelete = (id: string) => {
    mutate({ id });
  };

  return (
    <div className="container relative">
      <p className="text-lg font-semibold">API Key</p>
      <div className="mt-4 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="text-muted-foreground">
            {isNotEmpty(data) && `Total: ${data?.length}`}
          </div>
          <CreateAPIKey />
        </div>
        {/*  */}
        <div className="grid overflow-x-auto">
          <Table className="w-full table-fixed">
            <colgroup>
              <col className="w-full min-w-[200px]" />
              <col className="w-full min-w-[200px]" />
              <col className="w-[100px]" />
              {/* <col className="w-[100px]" /> */}
              <col className="w-[80px]" />
            </colgroup>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Api key</TableHead>
                <TableHead>Create Date</TableHead>
                {/* <TableHead>Last Used</TableHead> */}
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <TableCell key={index}>
                      <Skeleton className="h-4" />
                    </TableCell>
                  ))}
                </TableRow>
              )}
              {data &&
                data.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="truncate">
                      {item.api_key_name}
                    </TableCell>
                    <TableCell className="truncate font-medium">
                      <div className="flex items-center gap-1">
                        {formatAPIKey(item.api_key!)}
                        <CopyIconButton text={item.api_key!} />
                      </div>
                    </TableCell>
                    <TableCell>{fDate(item.create_date!)}</TableCell>
                    {/* <TableCell>{fDate(item.lastUsed)}</TableCell> */}
                    <TableCell>
                      <ConfirmationDialog
                        title="Delete API Key"
                        message="Are you sure you want to delete?"
                        onConfirm={() => handledDelete(item.api_key!)}
                        confirmText="Delete"
                        slots={{
                          confirmButton: {
                            variant: 'destructive',
                          },
                        }}
                      >
                        <Button variant="ghost" size={'icon'}>
                          <MdDeleteOutline className="h-4 w-4 text-destructive" />
                        </Button>
                      </ConfirmationDialog>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
            {data?.length === 0 && <TableCaption>No API key.</TableCaption>}
          </Table>
        </div>
      </div>
    </div>
  );
};

export default LLMCredentials;
